import { useEffect, useReducer, useRef, useState } from "react";
import { firebaseConfig } from "../util/FirebaseUtil";
import { addDoc, collection, getFirestore } from "firebase/firestore";
import { initializeApp } from "firebase/app";

const chunkSize = 1024 * 1023;
const sizeSteps = [3840, 2160, 1920, 1280, 1080, 720, 640, 512, 480, 256];

const ImageInjestButton = ({ style, onBase64, children }) => {
  const inputRef = useRef();

  const processImage = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();

      img.onload = function () {
        let width = img.width;
        let height = img.height;

        let modifiedSteps = [width >= height ? width : height, ...sizeSteps];

        const canvas = document.createElement("canvas");
        let ctx = canvas.getContext("2d");
        for (let maxDimension of modifiedSteps) {
          width = img.width;
          height = img.height;

          if (width > maxDimension) {
            height *= maxDimension / width;
            width = maxDimension;
          }

          if (height > maxDimension) {
            width *= maxDimension / height;
            height = maxDimension;
          }

          canvas.width = width;
          canvas.height = height;

          //ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          const dataUrl = canvas.toDataURL("image/jpeg", 0.7); // Adjust quality as needed
          console.log("Trying", maxDimension, "at", dataUrl.length / 1000);
          if (dataUrl.length / 1000 < 900) {
            onBase64(dataUrl);
            break;
          }
        }
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const filesSelected = (e) => {
    if (onBase64) {
      let fileArr = [...e.target.files];
      if (fileArr.length > 0) {
        processImage(fileArr[0]);
      }
    }
  };

  const selectFiles = (e) => {
    inputRef.current.click();
  };

  return (
    <button
      className="btn btn-primary"
      style={{ ...style }}
      onClick={selectFiles}
    >
      <input
        style={{ display: "none" }}
        type="file"
        accept="image/*"
        onChange={filesSelected}
        ref={inputRef}
      />
      {children}
    </button>
  );
};

const FileUploadButton = ({
  collection,
  style,
  children,
  onFiles,
  fileProperties,
}) => {
  const inputRef = useRef();

  const filesSelected = (e) => {
    if (onFiles) {
      let fileArr = [...e.target.files];
      if (fileArr.length > 0) {
        onFiles(
          fileArr.map((file) => {
            return { fileProperties, collection, file };
          })
        );
      }
    }
  };

  const selectFiles = (e) => {
    inputRef.current.click();
  };

  return (
    <button
      className="btn btn-primary"
      style={{ ...style }}
      onClick={selectFiles}
    >
      <input
        style={{ display: "none" }}
        type="file"
        multiple
        onChange={filesSelected}
        ref={inputRef}
      />
      {children}
    </button>
  );
};

const FileUpload = ({
  file,
  collection: collectionName,
  fileProperties,
  startUpload = true,
  style,
  onFileId,
}) => {
  const [firebaseDB, setFirebaseDB] = useState();
  const [status, setStatus] = useState("init");
  const [fileId, setFileId] = useState(null);
  const [fileSize, setFileSize] = useState();
  const [fileLoadedSize, setFileLoadedSize] = useState();
  const [completeChunks, setCompleteChunks] = useState();
  const [totalChunks, setTotalChunks] = useState();
  const [cancelRequested, setCancelRequested] = useState(false);
  const mainRef = useRef();

  const uploadFile = () => {
    setFileSize(file.size);

    const chunkCollectionRef = collection(firebaseDB, "chunks");
    const reader = new FileReader();
    reader.addEventListener("progress", (e) => {
      setFileLoadedSize(e.loaded);
    });
    reader.addEventListener("load", (e) => {
      const base64String = e.target.result;
      const dataLength = base64String.length;
      const chunkCount = Math.ceil(dataLength / chunkSize);
      setTotalChunks(chunkCount);

      let chunkNum = 0;

      const uploadChunk = async () => {
        console.log("Uploading chunk", chunkNum);
        const startIndex = chunkNum * chunkSize;
        const endIndex =
          chunkNum === chunkCount - 1 ? dataLength : (chunkNum + 1) * chunkSize;
        addDoc(chunkCollectionRef, {
          file_id: fileId,
          chunk_size: endIndex - startIndex,
          chunk_num: chunkNum,
          chunk_total: chunkCount,
          chunk_data: base64String.slice(startIndex, endIndex),
          collection: collectionName,
        }).then(() => {
          chunkNum++;
          setCompleteChunks(chunkNum);
        });
      };

      let lastChunkNum = -1;
      let uploadInterval = setInterval(() => {
        if (lastChunkNum < chunkNum) {
          lastChunkNum = chunkNum;
          console.log(mainRef.current);
          console.log(mainRef.current.getAttribute("cancelrequested"));
          if (mainRef.current.getAttribute("cancelrequested") === "true") {
            setStatus("cancelled");
            clearInterval(uploadInterval);
          } else if (chunkNum < chunkCount) {
            uploadChunk(chunkNum);
          } else {
            setStatus("complete");
            clearInterval(uploadInterval);
          }
        }
      }, 500);
    });

    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);
    setFirebaseDB(db);
  });

  useEffect(() => {
    if (completeChunks === 0) {
      uploadFile();
    }
  }, [completeChunks]);

  useEffect(() => {
    if (status === "init" && startUpload) {
      setStatus("prep");
    }
    if (status === "prep" && fileId === null && firebaseDB !== null) {
      const fileCollectionRef = collection(firebaseDB, "files");
      addDoc(fileCollectionRef, {
        ...fileProperties,
        file_name: file.name,
        file_modified: file.lastModified,
        file_size: file.size,
        file_type: file.type,
        collection: collectionName,
      }).then((docRef) => {
        const file_id = docRef.id;
        setFileId(file_id);
        if (onFileId) onFileId(file_id);
      });
    }
    if (status === "prep" && fileId !== null) {
      setCompleteChunks(0);
      setStatus("uploading");
    }
  }, [status, startUpload, firebaseDB, fileId]);

  return (
    <div
      cancelrequested={`${cancelRequested}`}
      ref={mainRef}
      style={{ ...style }}
    >
      <h4>{file.name}</h4>
      <p>
        {status === "init" && "Starting..."}
        {status === "prep" && "Preparing..."}
        {status === "uploading" && "Uploading..."}
        {status === "complete" && "Complete!"}
        {status === "cancelled" && <em>Cancelled</em>}
      </p>
      {status === "prep" && (
        <div
          className="progress"
          role="progressbar"
          aria-label="Prep Bar"
          aria-valuenow={fileLoadedSize}
          aria-valuemin="0"
          aria-valuemax={fileSize}
        >
          <div
            className={`progress-bar ${
              status === "cancelled" ? "text-bg-danger" : "text-bg-warning"
            }
            ${
              status === "prep" && "progress-bar-striped progress-bar-animated"
            }`}
            style={{
              width: `${Math.round((fileLoadedSize / fileSize) * 100)}%`,
            }}
          >
            {fileSize > 0 ? Math.round((fileLoadedSize / fileSize) * 100) : 0}%
          </div>
        </div>
      )}
      {(status === "uploading" || status === "cancelled") && (
        <div
          className="progress"
          role="progressbar"
          aria-label="Prep Bar"
          aria-valuenow={completeChunks}
          aria-valuemin="0"
          aria-valuemax={totalChunks}
        >
          <div
            className={`progress-bar text-bg-success
            ${status === "cancelled" ? "text-bg-danger" : "text-bg-success"}
            ${
              status === "uploading" &&
              "progress-bar-striped progress-bar-animated"
            }`}
            style={{
              width: `${Math.round((completeChunks / totalChunks) * 100)}%`,
            }}
          >
            {totalChunks > 0
              ? Math.round((completeChunks / totalChunks) * 100)
              : 0}
            %
          </div>
        </div>
      )}

      {status != "cancelled" && status != "complete" && (
        <>
          <br />
          <button
            className="btn btn-danger"
            onClick={() => {
              setCancelRequested(true);
            }}
          >
            {cancelRequested ? "Cancelling" : "Cancel"}
          </button>
        </>
      )}
    </div>
  );
};

export { FileUpload, FileUploadButton, ImageInjestButton };
